<script setup>
import { computed } from "vue";
import { ADULT_MEASUREMENTS, YOUTH_MEASUREMENTS } from "../misc/measurements";
import { SURFACES } from "../misc/constants";

const props = defineProps({
  getSurface: {
    type: Function,
    required: true
  },
  surfaceLabel: {
    type: String,
    required: true
  },
  downscaleFactor: {
    type: Number,
    required: true
  },
  shownCustomNeckLines: {
    type: Boolean,
    required: false,
    default: false
  },
  neckLineLabel: {
    type: String,
    required: false,
    default: ""
  },
  currentNeckLineIn: {
    type: String,
    required: false,
    default: "0.00"
  },
  currentNeckLinePx: {
    type: Number,
    required: false,
    default: 0
  }
});

const getStyles = (topY, topX, offsetHeight = 0) => ({
  top: `${topY * props.downscaleFactor}px`,
  left: `${topX * props.downscaleFactor}px`,
  height: `${props.currentNeckLinePx + offsetHeight}px`
});

const pivotStyles = computed(() => {
  const label =
    props.surfaceLabel === SURFACES.FRONT_LEFT_CHEST ? SURFACES.FRONT : props.surfaceLabel;
  const { top_x: topX, top_y: topY } = props.getSurface(label).surface_location_offset;
  return getStyles(topY, topX);
});

const chestStyles = computed(() => {
  const {
    top_x: topX,
    top_y: topY,
    bottom_y: bottomY
  } = props.getSurface(SURFACES.FRONT_LEFT_CHEST).surface_location_offset;
  const offsetHeight = ((bottomY - topY) * props.downscaleFactor) / 2;
  return getStyles(topY, topX, offsetHeight);
});

const isDark = computed(() => props.getSurface(props.surfaceLabel).is_dark);
const isYouth = computed(() => props.getSurface(props.surfaceLabel).is_youth);
const measurements = computed(() => (isYouth.value ? YOUTH_MEASUREMENTS : ADULT_MEASUREMENTS));
const locatedTop = computed(() => props.currentNeckLinePx < 30);
</script>

<template>
  <div
    class="artwork-measurement"
    :class="{
      'artwork-measurement--dark': isDark
    }"
    :style="pivotStyles"
  >
    <div
      v-if="props.shownCustomNeckLines"
      class="artwork-measurement__custom-legend"
      :class="{ 'artwork-measurement__custom-legend--top': locatedTop }"
    >
      <div class="artwork-measurement__legend-description-wrapper">
        <div class="artwork-measurement__legend-description">
          {{ props.neckLineLabel }}
        </div>
      </div>

      <div class="artwork-measurement__legend-title-wrapper">
        <div class="artwork-measurement__legend-invert-title">{{ props.currentNeckLineIn }}”</div>
      </div>
    </div>

    <div
      v-else
      class="artwork-measurement__legends"
      :class="{ 'artwork-measurement__legends--top': locatedTop }"
    >
      <div
        class="artwork-measurement__legend"
        :class="{
          'artwork-measurement__legend--youth': isYouth
        }"
      >
        <div class="artwork-measurement__legend-title">
          {{ measurements[props.surfaceLabel].legendLeft.title }}
        </div>

        <div class="artwork-measurement__legend-description-wrapper">
          <div class="artwork-measurement__legend-description">
            {{ measurements[props.surfaceLabel].legendLeft.description }}
          </div>
        </div>
      </div>

      <div v-if="measurements[props.surfaceLabel].legendRight" class="artwork-measurement__legend">
        <div class="artwork-measurement__legend-title">
          {{ measurements[props.surfaceLabel].legendRight.title }}
        </div>

        <div class="artwork-measurement__legend-description-wrapper">
          <div class="artwork-measurement__legend-description">
            {{ measurements[props.surfaceLabel].legendRight.description }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="props.surfaceLabel === SURFACES.FRONT_LEFT_CHEST"
    class="artwork-measurement-chest"
    :class="{
      'artwork-measurement-chest--dark': props.getSurface(props.surfaceLabel).is_dark
    }"
    :style="chestStyles"
  >
    <div class="artwork-measurement-chest__description">centered over far edge of collar</div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/print-settings";

.artwork-measurement,
.artwork-measurement-chest {
  user-select: none;
  pointer-events: none;
  position: absolute;
  margin-left: -1px;
  border-right-color: currentColor;
  border-right-width: 2px;
  color: $pod-gray-modern-900;

  &--dark {
    color: $white;
  }
}
.artwork-measurement {
  border-right-style: solid;

  &::before,
  &::after {
    width: 10px;
    height: 2px;
    position: absolute;
    left: -4px;
    background-color: currentColor;
    content: "";
  }

  &::before {
    top: 0;
  }

  &::after {
    bottom: 0;
  }

  &__legends {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    text-align: center;
    position: absolute;
    left: 50%;
    top: 50%;
    white-space: nowrap;
    transform: translate(-50%, -50%);

    &--top {
      top: auto;
      bottom: 100%;
      transform: translate(-50%, -5px);
    }
  }

  &__legend {
    font-size: 12px;
    font-weight: 700;

    &--youth {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-left: -80px;
    }
  }

  &__legend-title {
    line-height: 1;
    margin-bottom: 2px;
  }

  &__legend-invert-title {
    filter: invert(1);
    line-height: 22px;
  }

  &__legend-title-wrapper,
  &__legend-description-wrapper {
    border-radius: 5px;
    padding: 0 4px;
    background: currentColor;
  }

  &__legend-title-wrapper {
    min-width: 42px;
    filter: invert(1);
  }

  &__legend-description {
    filter: invert(1);
    line-height: 16px;
    text-transform: uppercase;
  }

  &__custom-legend {
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 10px;
    text-align: center;
    position: absolute;
    left: 50%;
    top: 50%;
    white-space: nowrap;
    transform: translate(-50%, -50%);
    margin-left: -14px;

    &--top {
      top: auto;
      bottom: 100%;
      transform: translate(-50%, -5px);
    }
  }
}

.artwork-measurement-chest {
  border-right-style: dashed;

  &__description {
    position: absolute;
    text-align: left;
    left: 10px;
    bottom: 24px;
    font-size: 12px;
    line-height: 1;
  }
}
</style>
